package er.prateek.crossly.config

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The `notifications` section of [AppConfig] -- every remotely-tunable knob for the smart
 * local-reminder feature (personalization, quiet hours, and the permission/soft-ask funnel's
 * timing). See that class for how it is fetched and parsed, and
 * [er.prateek.crossly.notifications.NotificationCoordinator] for how these are consumed.
 */
@Serializable
data class NotificationsConfig(
    /** Master kill switch -- off means no scheduling and no permission prompts at all. */
    @SerialName("enabled") val enabled: Boolean = true,
    @SerialName("streak_reminders_enabled") val streakRemindersEnabled: Boolean = true,
    @SerialName("daily_challenge_reminders_enabled") val dailyChallengeRemindersEnabled: Boolean = true,
    @SerialName("reengagement_reminders_enabled") val reengagementRemindersEnabled: Boolean = true,
    /** Samples needed before the personalized hour is trusted over [fallbackReminderHourLocal]. */
    @SerialName("min_open_history_for_personalization") val minOpenHistoryForPersonalization: Int = 4,
    /** How many recent app-open samples are kept for personalization. */
    @SerialName("open_history_window_size") val openHistoryWindowSize: Int = 14,
    /** Used verbatim until enough history has accumulated, and as a clamp fallback after. */
    @SerialName("fallback_reminder_hour_local") val fallbackReminderHourLocal: Int = 19,
    /** Reminder fires this many minutes after the player's usual open time, not right at it. */
    @SerialName("reminder_offset_minutes") val reminderOffsetMinutes: Int = 60,
    /** A computed hour inside this local-time window is pushed to [quietHoursEnd] instead. */
    @SerialName("quiet_hours_start") val quietHoursStart: Int = 22,
    @SerialName("quiet_hours_end") val quietHoursEnd: Int = 8,
    @SerialName("permission_ask_min_sessions") val permissionAskMinSessions: Int = 2,
    @SerialName("permission_ask_min_puzzles_completed") val permissionAskMinPuzzlesCompleted: Int = 1,
    /** Days between soft-ask bottom sheets shown after a denial. */
    @SerialName("soft_ask_cooldown_days") val softAskCooldownDays: Int = 7,
    @SerialName("max_soft_asks") val maxSoftAsks: Int = 3,
) {
    companion object {
        val DEFAULT = NotificationsConfig()
    }
}
